'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  Plus,
  Edit3,
  MapPin,
  Calendar,
  Save,
  Eye,
  ArrowLeft,
  Trash2,
  Globe,
  Image,
} from 'lucide-react';
import CompactHeader from '../CompactHeader';
import { useNavigate, Link } from '@tanstack/react-router';
import {
  Button,
  Input,
  Textarea,
  Badge,
  useRepository,
  Image as ImageComponent,
} from '@city-sage/aurum-avis-labs-components';
import { useForm } from 'react-hook-form';
import { Itinerary, Day, StopStateEnum, Stop } from '@city-sage/core-client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKeys } from '../../lib/QueryKeys';
import { useParams } from '@tanstack/react-router';
import { ChatButton } from '../chat/ChatButton';
import { MiniItineraryMap } from '../MiniItineraryMap';
import { ImageEditingSheet } from '../ImageEditingSheet';
import { ItineraryCoverImageSection } from '../ItineraryCoverImageSection';
import { ItineraryDayCard } from '../ItineraryDayCard';
import { useMultipleDayStops } from '../../hooks/useMultipleDayStops';
import { MarkupEditorSheet } from '../MarkupEditorSheet';
import confetti from 'canvas-confetti';

interface EditItineraryForm {
  title: string;
  shortDescription: string;
  description: string;
  highlights: string;
  visibility: 'PUBLIC' | 'PRIVATE';
  imageUrl?: string;
}

export default function ItineraryEditPage() {
  const { id } = useParams({ from: '/itineraries/$id/edit' });
  const navigate = useNavigate();
  const { api } = useRepository();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Image state
  const [isImageSheetOpen, setIsImageSheetOpen] = useState(false);

  // Markup editor state
  const [isMarkupEditorOpen, setIsMarkupEditorOpen] = useState(false);
  const [isShortDescMarkupEditorOpen, setIsShortDescMarkupEditorOpen] =
    useState(false);

  // Fetch itinerary and days
  const { data: itinerary, isLoading: isLoadingItinerary } = useQuery({
    queryKey: QueryKeys.itinerary.details(id),
    queryFn: () => api.findOneItinerary({ id }),
    enabled: !!id,
  });

  const { data: days, isLoading: isLoadingDays } = useQuery({
    queryKey: QueryKeys.itinerary.days(id),
    queryFn: () =>
      api.findAllItineraryDay({
        // TODO this is hardcoded for now, we need to fix this
        limit: 1000,
        itineraryId: id,
        sort: JSON.stringify(['number', 'ASC']),
      }),
    enabled: !!id,
  });

  // Fetch all stops for all days
  const { data: stopsData } = useMultipleDayStops({
    itineraryId: id || '',
    days,
  });

  // Form setup
  const form = useForm<EditItineraryForm>({
    defaultValues: {
      title: '',
      shortDescription: '',
      description: '',
      highlights: '',
      visibility: 'PRIVATE',
      imageUrl: '',
    },
  });


  const updateMutation = useMutation({
    mutationFn: (data: EditItineraryForm) => {
      return api.updateItinerary({
        id: id!,
        itinerary: { ...itinerary, ...data } as Omit<
          Itinerary,
          'id' | 'createdAt' | 'updatedAt' | 'account' | 'accountId'
        >,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QueryKeys.itinerary.details(id!),
      });
      toast.success('Changes saved successfully!');
    },
    onError: () => {
      toast.error('Failed to save changes. Please try again.');
    },
  });

  // Create day mutation
  const createDayMutation = useMutation({
    mutationFn: (data: {
      number: number;
      title?: string;
      description?: string;
    }) => {
      return api.createItineraryDay({
        itineraryId: id!,
        createDayDto: {
          number: data.number,
          title: data.title,
          description: data.description || '',
        },
      });
    },
    onSuccess: (newDay) => {
      queryClient.invalidateQueries({
        queryKey: QueryKeys.itinerary.days(id!),
      });
      toast.success(`Day ${newDay.number} added successfully!`);
      // Navigate to the edit page for the newly created day
      navigate({ to: `/itineraries/${id}/days/${newDay.id}/edit` });
    },
    onError: () => {
      toast.error('Failed to add day. Please try again.');
    },
  });

  // Toggle visibility mutation
  const toggleVisibilityMutation = useMutation({
    mutationFn: (visibility: 'PUBLIC' | 'PRIVATE') => {
      return api.updateItinerary({
        id: id!,
        itinerary: { ...itinerary, visibility } as Omit<
          Itinerary,
          'id' | 'createdAt' | 'updatedAt' | 'account' | 'accountId'
        >,
      });
    },
    onSuccess: (updatedItinerary) => {
      queryClient.invalidateQueries({
        queryKey: QueryKeys.itinerary.details(id!),
      });
      const action =
        updatedItinerary.visibility === 'PUBLIC' ? 'published' : 'unpublished';
      
      if (updatedItinerary.visibility === 'PUBLIC') {
        // Celebration for publishing!
        // Trigger confetti explosion
        confetti({
          particleCount: 150,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6']
        });
        
        // Add a second burst for more celebration
        setTimeout(() => {
          confetti({
            particleCount: 100,
            spread: 50,
            origin: { y: 0.7 },
            colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6']
          });
        }, 200);
        
        // Show congratulations message
        toast.success('🎉 Congratulations! Your itinerary has been published and is now live!', {
          duration: 4000,
        });
        
        // Navigate to the published itinerary after a brief delay
        setTimeout(() => {
          navigate({ to: '/itineraries/$id', params: { id: updatedItinerary.id } });
        }, 1500);
      } else {
        toast.success(`Itinerary ${action} successfully!`);
      }
    },
    onError: () => {
      toast.error('Failed to update visibility. Please try again.');
    },
  });

  const handleSubmit = form.handleSubmit(async (data) => {
    updateMutation.mutate(data);
  });

  const toggleVisibility = () => {
    if (!itinerary) return;
    const newVisibility =
      itinerary.visibility === 'PUBLIC' ? 'PRIVATE' : 'PUBLIC';
    toggleVisibilityMutation.mutate(newVisibility);
  };

  const addDay = () => {
    const newDayNumber = (days?.length || 0) + 1;
    createDayMutation.mutate({
      number: newDayNumber,
      title: `Day ${newDayNumber}`,
      description: '',
    });
  };

  const { register, watch, formState } = form;
  const formValues = watch();
  const { isDirty } = formState;


  // Reset form when itinerary loads and is not dirty
  useEffect(() => {
    if (itinerary && !isDirty) {
      form.reset({
        title: itinerary.title || '',
        shortDescription: itinerary.shortDescription || '',
        description: itinerary.description || '',
        highlights: itinerary.highlights || '',
        visibility: (itinerary.visibility as 'PUBLIC' | 'PRIVATE') || 'PRIVATE',
        imageUrl: itinerary.imageUrl || '',
      });
    }
  }, [itinerary, form, isDirty]);

  const hasUnacceptedSuggestions = useMemo(() => {
    return stopsData?.allStops.some((stop) => stop.state === 'SUGGESTED');
  }, [stopsData]);

  const hasNoStops = useMemo(() => {
    return !stopsData?.allStops.length || stopsData.allStops.length === 0;
  }, [stopsData]);

  if (isLoadingItinerary || isLoadingDays) {
    return (
      <div className="flex items-center justify-center min-h-[50dvh]">
        Loading...
      </div>
    );
  }

  if (!itinerary) {
    return (
      <div className="flex items-center justify-center min-h-[50dvh]">
        Itinerary not found
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-gray-50 pb-24">
      <CompactHeader
        icon={Edit3}
        title="Edit Itinerary"
        subtitle="Modify your travel plan"
      />

      <div className="space-y-2">
        <form onSubmit={handleSubmit} className="space-y-2">
          {/* Cover Image Section */}
          <div className="bg-white px-6 py-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-700">
                  Cover Image
                </h3>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (isDirty) {
                      alert(
                        'Please save your changes before editing the image'
                      );
                      return;
                    }
                    setIsImageSheetOpen(true);
                  }}
                  className="gap-2"
                >
                  <Image className="h-4 w-4" />
                  {itinerary.imageUrl ? 'Change Image' : 'Add Image'}
                </Button>
              </div>

              <div className="relative">
                {itinerary.imageUrl ? (
                  <div className="aspect-video rounded-lg overflow-hidden bg-gray-100">
                    <ImageComponent
                      src={itinerary.imageUrl}
                      image={itinerary.image}
                      alt="Itinerary cover"
                      className="w-full h-full object-cover"
                      imageClassName="h-full"
                    />
                  </div>
                ) : (
                  <div className="aspect-video border-2 border-dashed border-gray-200 flex items-center justify-center rounded-lg">
                    <div className="text-center">
                      <MapPin className="h-8 w-8 mx-auto mb-2 opacity-80" />
                      <p className="text-sm opacity-90">No cover image</p>
                      <p className="text-xs opacity-70">
                        Click "Add Image" to choose one
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Basic Info Section */}
          <div className="bg-white px-6 py-6">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-lg font-semibold">Overview</h2>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Itinerary Title
                </label>
                <Input
                  placeholder="Enter itinerary title..."
                  {...register('title')}
                  className="text-xl font-semibold"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Short Description
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsShortDescMarkupEditorOpen(true)}
                    className="text-xs underline"
                  >
                    Markup Editor
                  </Button>
                </div>
                <Textarea
                  placeholder="Write a short description of your itinerary..."
                  {...register('shortDescription')}
                  className="resize-none"
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* Additional Details */}
          <div className="bg-white px-6 py-6">
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Detailed Description
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsMarkupEditorOpen(true)}
                    className="text-xs underline"
                  >
                    Markup Editor
                  </Button>
                </div>
                <Textarea
                  placeholder="Provide a detailed description of your itinerary. What makes it special? What should travelers expect?"
                  {...register('description')}
                  rows={6}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* Trip Map Section */}
          <div className="bg-white px-6 py-4">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Trip Map</h2>
            </div>

            <MiniItineraryMap days={days} itineraryId={id} />
          </div>

          {/* Day Management Section */}
          <div className="bg-white px-6 py-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-semibold">Day by Day Itinerary</h2>
              </div>
              <Button
                type="button"
                onClick={addDay}
                size="sm"
                variant="outline"
                className="gap-1"
                disabled={createDayMutation.isPending}
              >
                <Plus className="h-4 w-4" />
                Add Day {(days?.length || 0) + 1}
              </Button>
            </div>

            <div className="space-y-6">
              {days && days.length > 0 ? (
                days.map((day) => (
                  <ItineraryDayCard key={day.id} day={day} itineraryId={id!} />
                ))
              ) : (
                <div className="p-6 border-2 border-dashed border-gray-200 rounded-lg text-center">
                  <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm text-gray-500">No days added yet</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Click "Add Day 1" to start planning your itinerary
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Save Button */}
          <div className="bg-white px-6 py-6">
            <div className="space-y-4">
              {/* Status Indicator */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${
                    itinerary?.visibility === 'PUBLIC' ? 'bg-green-500' : 'bg-yellow-500'
                  }`} />
                  <span className="text-sm font-medium">
                    Status: {itinerary?.visibility === 'PUBLIC' ? 'Published' : 'Draft'}
                  </span>
                </div>
                {itinerary?.visibility === 'PUBLIC' && (
                  <Badge variant="secondary" className="text-xs">
                    Live & Visible
                  </Badge>
                )}
              </div>

              {/* Save Changes Button - Secondary Action */}
              <Button
                type="submit"
                variant="secondary"
                className="w-full"
                disabled={updateMutation.isPending}
              >
                <Save className="h-4 w-4 mr-2" />
                {updateMutation.isPending ? 'Saving Draft...' : 'Save Draft'}
              </Button>

              {/* Primary Action - Publish */}
              <div className="space-y-2">
                {itinerary?.visibility === 'PRIVATE' || itinerary?.visibility === 'DRAFT' ? (
                  <Button
                    type="button"
                    className="w-full"
                    onClick={toggleVisibility}
                    disabled={
                      isDirty ||
                      hasNoStops ||
                      hasUnacceptedSuggestions ||
                      toggleVisibilityMutation.isPending
                    }
                  >
                    {toggleVisibilityMutation.isPending ? (
                      <>
                        <Globe className="h-4 w-4 mr-2" />
                        Publishing...
                      </>
                    ) : (
                      <>
                        <Globe className="h-4 w-4 mr-2" />
                        Publish Itinerary
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    className="w-full"
                    onClick={toggleVisibility}
                    disabled={toggleVisibilityMutation.isPending}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    {toggleVisibilityMutation.isPending
                      ? 'Unpublishing...'
                      : 'Unpublish & Make Private'}
                  </Button>
                )}


              </div>

              {/* Status Message - Show only one at a time based on priority */}
              {isDirty ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-800 font-medium">
                    You have unsaved changes
                  </p>
                  <p className="text-xs text-amber-700">
                    Save your draft first before publishing
                  </p>
                </div>
              ) : hasNoStops ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs text-red-800 font-medium">
                    No stops added yet
                  </p>
                  <p className="text-xs text-red-700">
                    Add at least one stop to your itinerary before publishing
                  </p>
                </div>
              ) : hasUnacceptedSuggestions ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs text-red-800 font-medium">
                    You have unaccepted suggestions
                  </p>
                  <p className="text-xs text-red-700">
                    Please accept or reject suggested stops before publishing
                  </p>
                </div>
              ) : itinerary?.visibility === 'PRIVATE' || itinerary?.visibility === 'DRAFT' ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800 font-medium">
                    Ready to share your itinerary?
                  </p>
                  <p className="text-xs text-blue-700">
                    Publishing will make it visible on the globe
                  </p>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-xs text-green-800 font-medium">
                    Your itinerary is live!
                  </p>
                  <p className="text-xs text-green-700">
                    Others can now discover and view your travel plan
                  </p>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>

      <ChatButton />

      {/* Image Editing Sheet */}
      {itinerary && formValues.title && formValues.title.length > 0 && (
        <ImageEditingSheet
          isOpen={isImageSheetOpen}
          onOpenChange={setIsImageSheetOpen}
          itinerary={itinerary}
          searchWord={formValues.title}
        />
      )}

      {/* Markup Editor Sheet for Short Description */}
      <MarkupEditorSheet
        isOpen={isShortDescMarkupEditorOpen}
        onOpenChange={setIsShortDescMarkupEditorOpen}
        value={formValues.shortDescription || ''}
        onSave={(value: string) => {
          form.setValue('shortDescription', value, { shouldDirty: true });
          setIsShortDescMarkupEditorOpen(false);
        }}
      />

      {/* Markup Editor Sheet */}
      <MarkupEditorSheet
        isOpen={isMarkupEditorOpen}
        onOpenChange={setIsMarkupEditorOpen}
        value={formValues.description || ''}
        onSave={(value: string) => {
          form.setValue('description', value, { shouldDirty: true });
          setIsMarkupEditorOpen(false);
        }}
      />
    </div>
  );
}
